import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  FormLabel,
  Alert,
  Box,
  Grid,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { normalizeTextPlus, normalizeText } from "./helpers/Helpers";
import { modificarNotas } from "./helpers/ContactPlusClient/ContactNClientsHelpers";
import Swal from "sweetalert2";

const getTextfieldStyle = (errorNotes) => {
  let borderColor = " 1px solid rgba(128, 128, 128, 0.35)";
  if (errorNotes === 1)
    borderColor = " 2px solid #13cc3bff"; // advertencia
  else if (errorNotes === 2) borderColor = " 2px solid #EF5350"; // error
  return {
    resize: "none",
    padding: "6px 13px",
    fontSize: "14px",
    border: ` ${borderColor}`,
    borderRadius: "10px",
    outline: "none",
  };
};

export default function ContactClientNotesComparisonDialog({
  notas,
  client,
  contact,
  setChangeRender = () => {},
  changeRender,
}) {
  const [open, setOpen] = useState(false);
  const [clienteNote, setClienteNote] = useState(notas?.[0]?.cliente || "");
  const [contactoNote, setContactoNote] = useState(notas?.[1]?.contacto || "");
  const [finalNote, setFinalNote] = useState("");
  const [errorNotes, setErrorNotes] = useState(0);
  const [canUnify, setCanUnify] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const handleOpen = () => {
    const cliente = notas?.[0]?.cliente || "";
    const contacto = notas?.[1]?.contacto || "";
    const notesAreAlreadyEqual =
      normalizeTextPlus(cliente) === normalizeTextPlus(contacto);

    setClienteNote(cliente);
    setContactoNote(contacto);
    setFinalNote(notesAreAlreadyEqual ? cliente : "");

    setErrorNotes(0);
    setCanUnify(false);
    setShowAlert(false);
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleConfirmUnification = async () => {
    if (canUnify) {
      Swal.fire({
        title: "Unificando notas...",
        text: "Por favor espere mientras se sincronizan los registros.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        await Promise.all([
          modificarNotas(contact?.id, finalNote, "contacto"),
          modificarNotas(client?.id, finalNote, "cliente"),
        ]);

        Swal.fire({
          icon: "success",
          title: "Notas unificadas correctamente",
          showConfirmButton: false,
          timer: 1500,
        });

        // console.log("hasta aqui llego");
        setChangeRender((prev) => !changeRender);
        handleClose();
      } catch (error) {
        console.error("   Error al unificar notas:", error);
        Swal.fire({
          icon: "error",
          title: "Error al unificar notas",
          text: "Por favor, inténtelo nuevamente.",
        });
      }
    } else {
      setShowAlert(true);
    }
  };

  useEffect(() => {
    if (!open) return;

    const hasFinalContent = Boolean(normalizeText(finalNote));
    setCanUnify(hasFinalContent);
    setErrorNotes(hasFinalContent ? 1 : 2);
    setShowAlert(!hasFinalContent);
  }, [finalNote, open]);

  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        onClick={handleOpen}
        sx={{ marginLeft: "20px" }}
      >
        CLIC PARA UNIFICAR NOTAS
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lf"
        fullWidth
        aria-labelledby="dialog-title"
      >
        <DialogTitle
          id="dialog-title"
          sx={{ display: "flex", alignItems: "center", gap: "10px" }}
        >
          <Warning color="warning" />
          UNIFICACIÓN DE NOTAS CLIENTE/CONTACTO
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            Seleccione una nota como base o escriba manualmente la nota final
            unificada.
          </Typography>

          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            ¿Cómo funciona la nota final unificada?
          </Typography>
          <Box component="ol" sx={{ pl: 2, mb: 0 }}>
            <li>
              <Typography component="span" variant="body2">
                Revise la nota del cliente y la nota del contacto.
              </Typography>
            </li>
            <li>
              <Typography component="span" variant="body2">
                Puede pulsar "Elegir esta nota" para copiar una de ellas a la
                nota final.
              </Typography>
            </li>
            <li>
              <Typography component="span" variant="body2">
                Complete la "Nota final unificada" incluyendo toda la
                información relevante de ambas notas.
              </Typography>
            </li>
            <li>
              <Typography component="span" variant="body2">
                La "Nota final unificada" será el texto final que quedará
                visualizado en ambos registros después de guardar.
              </Typography>
            </li>
          </Box>

          <Grid container>
            <Grid item xs={6} p={1}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormLabel sx={{ mb: "10px" }}>
                  Nota principal del cliente
                </FormLabel>
                <textarea
                  style={getTextfieldStyle(errorNotes)}
                  rows={11}
                  value={clienteNote}
                  readOnly
                />
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  color="primary"
                  onClick={() => setFinalNote(clienteNote)}
                >
                  Elegir esta nota
                </Button>
              </FormControl>
            </Grid>
            <Grid item xs={6} p={1}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: "10px" }}>
                  Nota principal del contacto
                </FormLabel>
                <textarea
                  style={getTextfieldStyle(errorNotes)}
                  rows={11}
                  value={contactoNote}
                  readOnly
                />
                <Button
                  sx={{ mt: 1 }}
                  variant="outlined"
                  color="primary"
                  onClick={() => setFinalNote(contactoNote)}
                >
                  Elegir esta nota
                </Button>
              </FormControl>
            </Grid>

            <Grid item xs={12} p={1}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: "10px" }}>Nota final unificada</FormLabel>
                <textarea
                  style={getTextfieldStyle(errorNotes)}
                  rows={10}
                  value={finalNote}
                  onChange={(e) => setFinalNote(e.target.value)}
                />
              </FormControl>
            </Grid>
          </Grid>

          {showAlert && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="warning">
                Debe seleccionar o escribir la nota final antes de guardar.
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmUnification}
            disabled={!canUnify ? true : false}
            color={canUnify ? "success" : "primary"}
            variant="contained"
          >
            {canUnify ? "Guardar" : "No disponible"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
